import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import {
  BarChart,
  PieChart,
  Calendar,
  Clock,
  Users,
  BookOpen,
  ArrowLeft,
} from "lucide-react";
import { Button } from "src/components/ui/Button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "src/components/ui/Card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/Select";

const SessionAnalytics = () => {
  const navigate = useNavigate();
  const { setTitle, setDescription, setActions, reset } = usePageHeaderStore();

  useEffect(() => {
    setTitle("Session Analytics");
    setDescription("View detailed statistics and reports about tutoring sessions");
    setActions(
      <Button
        variant="outline"
        size="sm"
        onClick={() => navigate("/admin/sessions")}
        className="flex items-center gap-1"
      >
        <ArrowLeft size={16} />
        Back to Sessions
      </Button>
    );

    return () => reset();
  }, [setTitle, setDescription, setActions, reset, navigate]);

  return (
    <>
      <div className="space-y-6">
        <div className="flex justify-end">
            <Select defaultValue="last30days">
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select time period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="last7days">Last 7 days</SelectItem>
                <SelectItem value="last30days">Last 30 days</SelectItem>
                <SelectItem value="last90days">Last 90 days</SelectItem>
                <SelectItem value="thisyear">This year</SelectItem>
                <SelectItem value="alltime">All time</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <StatCard
              title="Total Sessions"
              value="245"
              icon={<Calendar className="h-6 w-6" />}
              description="Last 30 days"
              change="+12.5%"
              positive={true}
            />
            <StatCard
              title="Avg. Session Duration"
              value="58 min"
              icon={<Clock className="h-6 w-6" />}
              description="Last 30 days"
              change="+4.3%"
              positive={true}
            />
            <StatCard
              title="Active Students"
              value="156"
              icon={<Users className="h-6 w-6" />}
              description="Last 30 days"
              change="+18.2%"
              positive={true}
            />
            <StatCard
              title="Popular Subjects"
              value="12"
              icon={<BookOpen className="h-6 w-6" />}
              description="With >10 sessions"
              change="+2.5%"
              positive={true}
            />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <Card>
              <CardHeader>
                <CardTitle>Sessions by Subject</CardTitle>
                <CardDescription>
                  Distribution of sessions across different subjects
                </CardDescription>
              </CardHeader>
              <CardContent className="h-80 flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <PieChart className="h-12 w-12 mx-auto mb-4 text-rfpurple-500" />
                  <p>Chart visualization would appear here</p>
                  <p className="text-sm mt-2">
                    Top subjects: Mathematics, Computer Science, Physics
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Sessions Over Time</CardTitle>
                <CardDescription>
                  Number of sessions conducted per week
                </CardDescription>
              </CardHeader>
              <CardContent className="h-80 flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <BarChart className="h-12 w-12 mx-auto mb-4 text-rfpurple-500" />
                  <p>Chart visualization would appear here</p>
                  <p className="text-sm mt-2">
                    Trend: 15% increase in weekly sessions
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Additional Analytics */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Session Completion Rate</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <div className="text-5xl font-bold text-rfpurple-600 mb-2">
                    94%
                  </div>
                  <p className="text-sm text-gray-500">
                    Sessions completed successfully
                  </p>
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mt-4">
                    <div
                      className="bg-rfpurple-600 h-2.5 rounded-full"
                      style={{ width: "94%" }}
                    ></div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Average Ratings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <div className="text-5xl font-bold text-rfpurple-600 mb-2">
                    4.8
                  </div>
                  <p className="text-sm text-gray-500">
                    Average session rating (out of 5)
                  </p>
                  <div className="flex justify-center mt-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg
                        key={star}
                        className={`w-5 h-5 ${
                          star <= 4 ? "text-yellow-400" : "text-yellow-200"
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                      </svg>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Peak Session Times</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium">
                        Afternoons (2-5 PM)
                      </span>
                      <span className="text-sm font-medium">45%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-rfpurple-600 h-2 rounded-full"
                        style={{ width: "45%" }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium">
                        Evenings (5-8 PM)
                      </span>
                      <span className="text-sm font-medium">30%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-rfpurple-600 h-2 rounded-full"
                        style={{ width: "30%" }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium">
                        Mornings (9-12 AM)
                      </span>
                      <span className="text-sm font-medium">20%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-rfpurple-600 h-2 rounded-full"
                        style={{ width: "20%" }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium">
                        Late Night (8-11 PM)
                      </span>
                      <span className="text-sm font-medium">5%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-rfpurple-600 h-2 rounded-full"
                        style={{ width: "5%" }}
                      ></div>
                    </div>
                  </div>
                </div>
              </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

// Helper component
const StatCard = ({ title, value, icon, description, change, positive }) => (
  <div className="bg-white p-6 rounded-lg shadow-md">
    <div className="flex justify-between items-center">
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <p className="text-3xl font-bold">{value}</p>
        <p className="text-sm text-gray-500">{description}</p>
        <p
          className={`text-sm ${positive ? "text-green-500" : "text-red-500"}`}
        >
          {change}
        </p>
      </div>
      <div className="text-rfpurple-500">{icon}</div>
    </div>
  </div>
);

export default SessionAnalytics;
